import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import useFitText from './useFitText';
import logoUrl from '../assets/garage-project-logo.svg';
import freshClubLogoUrl from '../assets/fresh-club-logo-filled.svg';

function HeaderChrome() {
  return (
    <header className="lp-header">
      <div className="lp-header-inner">
        <div className="lp-header-side">
          <div className="lp-hamburger" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <img
            className="lp-fresh-logo"
            src={freshClubLogoUrl}
            alt="FRESH Club"
          />
        </div>
        <div className="lp-header-side">
          <svg className="lp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <svg className="lp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <svg className="lp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 6h15l-1.5 9h-12z" />
            <circle cx="9" cy="20" r="1" />
            <circle cx="18" cy="20" r="1" />
            <path d="M6 6L5 3H2" />
          </svg>
        </div>
      </div>
    </header>
  );
}

function ExpandedLogo({ logoColor }) {
  return (
    <div className="lp-logo-overlay">
      <div className="lp-logo-overlay-inner">
        <span className="lp-logo-center">
          <span
            className="lp-logo-link"
            style={{
              color: logoColor,
              '--logo-mask': `url("${logoUrl}")`,
            }}
            role="img"
            aria-label="Garage Project"
          />
        </span>
      </div>
    </div>
  );
}

ExpandedLogo.propTypes = {
  logoColor: PropTypes.string.isRequired,
};

function OverlayText({ text }) {
  const [resizeComplete, setResizeComplete] = useState(false);
  const { ref, fontSize } = useFitText({
    onFinish: () => setResizeComplete(true),
  });

  const lines = useMemo(() => (text || '').split('\n'), [text]);

  return (
    <h1
      ref={ref}
      className={`lp-overlay-text ${resizeComplete ? 'lp-overlay-text--visible' : 'lp-overlay-text--hidden'}`}
      style={{ fontSize }}
    >
      {lines.map((line, lineIndex) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={lineIndex} className="lp-overlay-line">
          {[...line].map((character, characterIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <span key={characterIndex} className="lp-overlay-char">
              {character === ' ' ? '\u00a0' : (
                <span className="lp-overlay-char-inner">{character}</span>
              )}
            </span>
          ))}
        </div>
      ))}
    </h1>
  );
}

OverlayText.propTypes = {
  text: PropTypes.string.isRequired,
};

function LandingPreviewDocument({ config }) {
  const {
    backgroundImageUrl,
    overlay,
    text,
    overlayImageUrl,
    logoColor,
    showAnnouncementBanner,
    primaryButton,
    secondaryButton,
  } = config;

  const buttons = [primaryButton, secondaryButton].filter((button) => button && button.enabled);

  return (
    <div className={`lp-shell${showAnnouncementBanner ? ' lp-shell--with-banner' : ''}`}>
      {showAnnouncementBanner && (
        <div className="lp-announcement">Free Shipping</div>
      )}
      <HeaderChrome />
      {/* Fixed over hero (iframe viewport) — same placement as live Header logo */}
      <ExpandedLogo logoColor={logoColor} />
      <div className="lp-main">
        <div className="lp-content-pad">
          <div className="lp-hero">
            <div className="lp-bg-layer">
              {backgroundImageUrl && (
                <img
                  className="lp-bg-img"
                  src={backgroundImageUrl}
                  alt="Landing page background"
                />
              )}
            </div>
            <div className="lp-content-layer">
              <div className="lp-homepage">
                <div className="lp-spacer" />
                <span className="lp-overlay-wrap">
                  {overlay === 'image' && overlayImageUrl && (
                    <div className="lp-overlay-image-wrap">
                      <img
                        className="lp-overlay-image"
                        src={overlayImageUrl}
                        alt="Landing page overlay"
                      />
                    </div>
                  )}
                  {overlay === 'text' && (
                    <OverlayText key={text || ''} text={text || ''} />
                  )}
                </span>
                <div className="lp-ctas">
                  {buttons.map((button, index) => (
                    // Live site uses <a class="btn btn--black"> — match sizing/UA styles
                    <a
                      className="btn btn--black"
                      href={button.path || '#'}
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${index}:${button.label}`}
                      onClick={(event) => event.preventDefault()}
                    >
                      {button.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

LandingPreviewDocument.propTypes = {
  config: PropTypes.shape({
    backgroundImageUrl: PropTypes.string,
    overlay: PropTypes.oneOf(['text', 'image']).isRequired,
    text: PropTypes.string,
    overlayImageUrl: PropTypes.string,
    logoColor: PropTypes.string.isRequired,
    showAnnouncementBanner: PropTypes.bool,
    primaryButton: PropTypes.shape({
      enabled: PropTypes.bool,
      label: PropTypes.string,
      path: PropTypes.string,
    }).isRequired,
    secondaryButton: PropTypes.shape({
      enabled: PropTypes.bool,
      label: PropTypes.string,
      path: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

export default LandingPreviewDocument;
